// At the top of DoodlePage.js, add this line:
import './DoodlePage.css';

// src/pages/DoodlePage.js
import React, { useState, useRef, useEffect, useCallback } from 'react';

const DoodlePage = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [accuracy, setAccuracy] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [points, setPoints] = useState([]);
  const [showTarget, setShowTarget] = useState(true);

  // Shape definitions with increasing complexity
  const shapes = [
    { 
      id: 1, 
      name: "Straight Line", 
      description: "Draw a straight horizontal line",
      difficulty: "Beginner",
      targetPoints: [[100, 200], [300, 200]],
      tolerance: 15
    },
    { 
      id: 2, 
      name: "Vertical Line", 
      description: "Draw a straight vertical line",
      difficulty: "Beginner",
      targetPoints: [[200, 100], [200, 300]],
      tolerance: 15
    },
    { 
      id: 3, 
      name: "Triangle", 
      description: "Draw a triangle",
      difficulty: "Easy",
      targetPoints: [[200, 100], [150, 180], [250, 180], [200, 100]],
      tolerance: 20
    },
    { 
      id: 4, 
      name: "Square", 
      description: "Draw a square",
      difficulty: "Easy",
      targetPoints: [[150, 150], [250, 150], [250, 250], [150, 250], [150, 150]],
      tolerance: 20
    },
    { 
      id: 5, 
      name: "Circle", 
      description: "Draw a circle",
      difficulty: "Medium",
      targetPoints: generateCirclePoints(200, 175, 50),
      tolerance: 25
    },
    { 
      id: 6, 
      name: "Pentagon", 
      description: "Draw a pentagon (5 sides)",
      difficulty: "Medium",
      targetPoints: generatePolygonPoints(200, 175, 50, 5),
      tolerance: 25
    },
    { 
      id: 7, 
      name: "Hexagon", 
      description: "Draw a hexagon (6 sides)",
      difficulty: "Hard",
      targetPoints: generatePolygonPoints(200, 175, 50, 6),
      tolerance: 25
    },
    { 
      id: 8, 
      name: "Star", 
      description: "Draw a 5-pointed star",
      difficulty: "Expert",
      targetPoints: generateStarPoints(200, 175, 50, 5),
      tolerance: 30
    }
  ];

  const currentShape = shapes[currentLevel - 1];

  // Generate circle points
  function generateCirclePoints(centerX, centerY, radius, numPoints = 36) {
    const points = [];
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      points.push([x, y]);
    }
    return points;
  }

  // Generate polygon points
  function generatePolygonPoints(centerX, centerY, radius, sides) {
    const points = [];
    for (let i = 0; i <= sides; i++) {
      const angle = (i / sides) * 2 * Math.PI - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      points.push([x, y]);
    }
    return points;
  }

  // Generate star points
  function generateStarPoints(centerX, centerY, outerRadius, points) {
    const coords = [];
    const innerRadius = outerRadius * 0.4;
    
    for (let i = 0; i <= points * 2; i++) {
      const angle = (i / (points * 2)) * 2 * Math.PI - Math.PI / 2;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      coords.push([x, y]);
    }
    coords.push(coords[0]); // Close the star
    return coords;
  }

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    
    // Clear canvas and draw target shape if enabled
    clearCanvas();
    if (showTarget) {
      drawTargetShape();
    }
  }, [currentLevel, showTarget]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setPoints([]);
    setAccuracy(0);
    setCompleted(false);
    
    // Redraw the target shape guide if it's enabled
    if (showTarget) {
      drawTargetShape();
    }
  };

  const drawTargetShape = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const targetPoints = currentShape.targetPoints;

    ctx.save();
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    ctx.beginPath();
    ctx.moveTo(targetPoints[0][0], targetPoints[0][1]);
    for (let i = 1; i < targetPoints.length; i++) {
      ctx.lineTo(targetPoints[i][0], targetPoints[i][1]);
    }
    ctx.stroke();
    ctx.restore();
  };

  // Get coordinates from either mouse or touch event
  const getEventCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
    
    // Handle mouse events
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    setIsDrawing(true);
    
    const { x, y } = getEventCoordinates(e);
    const newPoint = [x, y];
    setPoints([newPoint]);
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    
    const { x, y } = getEventCoordinates(e);
    const newPoint = [x, y];
    setPoints(prev => [...prev, newPoint]);
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#128C7E';
    ctx.lineWidth = 3;
    ctx.setLineDash([]);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
    setAttempts(prev => prev + 1);
    calculateAccuracy();
  };

  const calculateAccuracy = () => {
    if (points.length < 2) return;
  
    const targetPoints = currentShape.targetPoints;
    const tolerance = currentShape.tolerance;
    let totalDistance = 0;
    let validPoints = 0;
  
    console.log(`Calculating accuracy for ${points.length} drawn points`);
  
    // Simple distance-based accuracy calculation
    for (const drawnPoint of points) {
      let minDistance = Infinity;
      for (const targetPoint of targetPoints) {
        const distance = Math.sqrt(
          Math.pow(drawnPoint[0] - targetPoint[0], 2) + 
          Math.pow(drawnPoint[1] - targetPoint[1], 2)
        );
        minDistance = Math.min(minDistance, distance);
      }
      
      if (minDistance <= tolerance) {
        validPoints++;
      }
      totalDistance += minDistance;
    }
  
    // Improved accuracy calculation - more forgiving
    const proximityScore = (validPoints / points.length) * 100;
    const distanceScore = Math.max(0, 100 - (totalDistance / points.length));
    const accuracyScore = Math.max(0, Math.min(100, (proximityScore + distanceScore) / 2));
    
    const finalAccuracy = Math.round(accuracyScore);
    console.log(`Accuracy calculated: ${finalAccuracy}% (${validPoints}/${points.length} valid points)`);
    
    setAccuracy(finalAccuracy);
    
    // Changed from 70% to 50% requirement
    if (finalAccuracy >= 50) {
      setCompleted(true);
      console.log("Level completed!");
    }
  };

  const nextLevel = () => {
    if (currentLevel < shapes.length) {
      setCurrentLevel(prev => prev + 1);
      setAttempts(0);
      setAccuracy(0);
      setCompleted(false);
      clearCanvas();
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Easy': return 'text-blue-600 bg-blue-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Hard': return 'text-orange-600 bg-orange-100';
      case 'Expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Drawing Skills Practice
        </h1>
        <p className="text-gray-600">
          Practice fine motor skills through progressive drawing exercises
        </p>
      </div>

      {/* Level Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Level {currentLevel}: {currentShape.name}
            </h2>
            <p className="text-gray-600">{currentShape.description}</p>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(currentShape.difficulty)}`}>
              {currentShape.difficulty}
            </span>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-lg p-3">
            <div className="text-2xl font-bold text-blue-600">{currentLevel}</div>
            <div className="text-sm text-gray-600">Current Level</div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <div className="text-2xl font-bold text-green-600">{accuracy}%</div>
            <div className="text-sm text-gray-600">Accuracy</div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <div className="text-2xl font-bold text-purple-600">{attempts}</div>
            <div className="text-sm text-gray-600">Attempts</div>
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mb-6">
        <canvas
          ref={canvasRef}
          width={400}
          height={350}
          className="border border-gray-300 rounded cursor-crosshair mx-auto block touch-none"
          style={{ touchAction: 'none' }} // Prevent default touch behaviors
          // Mouse events
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          // Touch events
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 justify-center mb-6">
        <button
          onClick={clearCanvas}
          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <span className="material-icons">refresh</span>
          Clear & Try Again
        </button>
        
        <button
          onClick={() => setShowTarget(!showTarget)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showTarget 
              ? 'bg-blue-500 text-white hover:bg-blue-600' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <span className="material-icons">visibility</span>
          {showTarget ? 'Hide' : 'Show'} Guide
        </button>

        {completed && (
          <button
            onClick={nextLevel}
            disabled={currentLevel >= shapes.length}
            className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span className="material-icons">check_circle</span>
            {currentLevel >= shapes.length ? 'All Levels Complete!' : 'Next Level'}
            {currentLevel < shapes.length && <span className="material-icons">arrow_forward</span>}
          </button>
        )}
      </div>

      {/* Feedback */}
      {accuracy > 0 && (
        <div className={`text-center p-4 rounded-lg ${
          completed 
            ? 'bg-green-100 text-green-800' 
            : accuracy >= 50 
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
        }`}>
          {completed ? (
            <div className="flex items-center justify-center gap-2">
              <span className="material-icons text-yellow-500">star</span>
              <span className="font-semibold">
                Excellent! You've mastered the {currentShape.name}!
              </span>
              <span className="material-icons text-yellow-500">star</span>
            </div>
          ) : accuracy >= 50 ? (
            `Good effort! Accuracy: ${accuracy}%. Try to get 50% or higher to advance.`
          ) : (
            `Keep practicing! Accuracy: ${accuracy}%. You can do this!`
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">How to Play:</h3>
        <ul className="text-blue-700 space-y-1 text-sm">
          <li>• Use your finger (on mobile/tablet) or mouse (on computer) to draw the target shape</li>
          <li>• The dotted gray lines show you the target shape (toggle with "Show/Hide Guide")</li>
          <li>• Try to match the shape as closely as possible</li>
          <li>• Achieve 50% accuracy or higher to unlock the next level</li>
          <li>• Practice makes perfect - don't worry about making mistakes!</li>
          <li>• On mobile: Use one finger to draw, avoid resting your palm on the screen</li>
        </ul>
      </div>
    </div>
  );
};

export default DoodlePage;